import React, { useContext, useEffect, useState } from 'react';
import Card from 'src/components/common/Card.redesign';
import Ownership from 'src/models/tract_detail/Ownership';
import FormTextbox, { FormTextBoxDataType } from 'src/components/controls/forms/redesign/FormTextbox';
import { SelectItem } from 'src/models/common/SelectItem';
import FormSelect from 'src/components/controls/forms/redesign/FormFilterSelect';
import AdditionalOwnershipCard from './AdditionalOwnershipCard';
import AddressUtil from 'src/util/Address';
import { AddlOwnershipRatifyList, ExcludeMineralOwnerList } from 'src/models/tract_detail/AdditionalOwnershipType';
import AnimateHeight from 'react-animate-height';
import { ContextMenuAction } from 'src/components/common/ContextMenu';
import * as Constants from 'src/components/common/Constants';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import { AppContext } from 'src/providers/AppContextProvider';
import { IAppContextState } from 'src/reducers/AppContext';
import OwnerUtil from 'src/util/OwnerUtil';
import { convertInstrumentTypesToSelectList } from 'src/models/InstrumentType';
import AttachedAgreements from '../owners/AttachedAgreements';
import AgreementUtil from 'src/util/AgreementUtil';
import OwnershipFormUtil from './OwnershipFormUtil';
import { OwnershipCategoryType } from 'src/models/OwnershipCategory';
// import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';

interface IOwnershipCardProps {
    ownership: Ownership;
    index: number;
    contactCategories: Array<SelectItem>;
    categoryTypes: Array<OwnershipCategoryType>;
    leaseBuyers: Array<SelectItem>;
    addAdditionalOwnership: (parentOwnershipId: number) => void;
    deleteOwnership: (ownershipId: number) => void;
    navigateLeaseInfo: (ownershipId: number) => void;
    openOwnershipNotes: (ownershipId: number) => void;
    makeMineralOwnership: (ownershipId: number) => void;
    // TODO: implement the below
    deleteAdditionalOwnerships: (ownershipId: number) => void;
    onInterestFocus: (event: React.FocusEvent<HTMLInputElement>) => void;
    onInterestChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    hasNonExecutiveAdditionalOwnership: boolean;
    navigateAcquisition: (ownershipId: number, ownerLookupId: number) => void;
    openContactNotes: (ownership: Ownership) => void;
    isCommittedCategoriesHidden: boolean;
    onRatifyAdditionalOwner?: (ownershipId: number, addlCopyId: number) => void;
}

// Instrument types are static; build the select list once at module load
// instead of rebuilding it on every render of every card.
const instrumentTypeItems = convertInstrumentTypesToSelectList();

const OwnershipCard = (props: IOwnershipCardProps) => {
    const [additionalHeight, setAdditionalHeight] = useState<string | number>(0);
    const [vestingHeight, setVestingHeight] = useState<string | number>(0);
    // Additional ownerships start collapsed, and AnimateHeight only sets the container
    // height to 0 - it still mounts its children. On a horizon where the parents carry
    // many additional owners that meant hundreds of invisible cards (~12 form-bound
    // fields each) registering with final-form on load. Mount them on first expand
    // instead, then keep them mounted so collapsing stays instant.
    const [hasExpandedAdditional, setHasExpandedAdditional] = useState<boolean>(false);
    const [isClientLeased, setIsClientLeased] = useState<boolean>(false);
    const [localContactCategories, setLocalContactCategories] = useState<Array<SelectItem>>(undefined);

    const dispatch = useDispatch();
    const appContext = useContext<IAppContextState>(AppContext);

    /**
     * @returns true if ownership has an active lease AND that lease has a WI owner who has
     *  been selected as "This is the client" in the owner screen.
     */
    useEffect(() => {
        setIsClientLeased(props?.ownership?.isWIClientAcreage === true);
    }, [props.ownership]);

    useEffect(() => {
        setLocalContactCategories(props?.contactCategories?.filter((type: SelectItem) =>
            props?.isCommittedCategoriesHidden  && type?.id !== props?.ownership?.ownershipContactCategoryId ? !type?.name?.includes('Committed') : true
        ));
    }, [props.contactCategories]);

    const navigateToOwnerlistPage = (ownership: Ownership) => {
        if (!!ownership) {
            localStorage.setItem('ownerPage_SelectedOwner', ownership?.ownerLookUpId?.toString());
            dispatch(push(`/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/owners`));
        }
    };

    const navToLeasehold = (ownershipId: number, contractId: number, contractholdId: number, contractType: string) => {
        let parentPath = window.location.pathname;
        let navPath = AgreementUtil.navToLeasehold(ownershipId, contractId, contractholdId, contractType);
        dispatch(push(parentPath + navPath));
    };

    const actions: ContextMenuAction[] = [{ text: 'Add Additional Ownership', action: () => props.addAdditionalOwnership(props.ownership.id) }];
    if (props.ownership?.additionalOwnerships?.length > 0) {
        actions.push({ text: 'Delete Additional Ownerships', action: () => props.deleteAdditionalOwnerships(props.ownership.id) });
    }
    actions.push(
        ...[
            { text: 'Delete Ownership', action: () => props.deleteOwnership(props.ownership.id) },
            { text: 'Manage Contract Ownership', action: () => props.navigateLeaseInfo(props.ownership.id) },
            { text: 'Ownership Notes', action: () => props.openOwnershipNotes(props.ownership.id) },
            { text: 'Kudu Agreements', action: () => props.navigateAcquisition(props.ownership.id, props.ownership.ownerLookUpId) },
            { text: 'Contact Notes', action: () => props.openContactNotes(props.ownership) },
            { text: 'Owner List', action: () => navigateToOwnerlistPage(props.ownership) },
        ]
    );

    const additionalOwnership = () => {
        return props.ownership.additionalOwnerships.map((ao, index) => {
            const additionalOwner = ao.ownerInfo;
            const additionalOwnershipActions: ContextMenuAction[] = [];

            if (!ExcludeMineralOwnerList.includes(ao.additionalOwnershipTypeId)) {
                additionalOwnershipActions.push({ text: 'Make Mineral owner', action: () => props.makeMineralOwnership(ao.id) });
            }

            if (AddlOwnershipRatifyList.includes(ao.additionalOwnershipTypeId)) {
                additionalOwnershipActions.push({ text: 'Ratify', action: () => props.onRatifyAdditionalOwner?.(ao.id, props.ownership.id) });
            }

            additionalOwnershipActions.push(
                { text: 'Delete Ownership', action: () => props.deleteOwnership(ao.id) },
                { text: 'Manage Contract Ownership', action: () => props.navigateLeaseInfo(ao.id) },
                { text: 'Ownership Notes', action: () => props.openOwnershipNotes(ao.id) },
                { text: 'Kudu Agreements', action: () => props.navigateAcquisition(ao.id, ao.ownerLookUpId) },
                { text: 'Contact Notes', action: () => props.openContactNotes(ao) },
            );

            const badgeStyle: React.CSSProperties = {
                position: 'relative',
                marginTop: '-1px',
                marginLeft: '-1px',
            };

            return (
                <AdditionalOwnershipCard
                    badge={ao.id.toString()}
                    isHighlighted={isClientLeased}
                    badgeStyle={badgeStyle}
                    rightBadgePanel={ownershipBadgeInfo(ao.activeLeaseTypes ?? [], ao.leasedStatus ?? '')}
                    key={index}
                    actions={additionalOwnershipActions}
                    contactCategories={props.contactCategories}
                    categoryTypes={props.categoryTypes}
                    leaseBuyers={props.leaseBuyers}
                    additionalOwnership={ao}
                    parentIndex={props.index}
                    index={index}
                    additionalOwner={additionalOwner}
                    onInterestChange={props.onInterestChange}
                    onInterestFocus={props.onInterestFocus}
                    hasNonExecutiveAdditionalOwnership={props.hasNonExecutiveAdditionalOwnership}
                    isCommittedCategoriesHidden={props.isCommittedCategoriesHidden}
                />
            );
        });
    };

    const vestingDeedRender = () => {
        return (
            <div className="mr-2 ml-2" style={{ display: 'flex', justifyContent: 'space-between' }}>
                <FormTextbox
                    label="Vesting Deed Reference"
                    name={`ownerships[${props.index}].vestingDeedReference`}
                    style={{ width: '22%' }}
                />
                <FormTextbox
                    label="Vesting Deed Date"
                    name={`ownerships[${props.index}].vestingDeedDate`}
                    type={FormTextBoxDataType.Date}
                    style={{ width: '22%' }}
                />
                <FormSelect
                    label="Vesting Deed Instrument Type"
                    name={`ownerships[${props.index}].vestingDeedInstrumentType`}
                    items={instrumentTypeItems}
                    style={{ width: '22%' }}

                />
                <FormTextbox
                    label="Vesting Deed Instrument Source"
                    name={`ownerships[${props.index}].vestingDeedInstrumentSource`}
                    style={{ width: '22%' }}
                />
            </div>
        );
    };

    const ownershipBadgeInfo = (activeLeaseTypes: Array<string>, leasedStatus: string) => {
        return (
            <div style={{ fontSize: '1.4rem', position: 'relative', marginTop: '.2rem', marginRight: '2rem', whiteSpace: 'nowrap' }}>
                <span style={{ display: 'inline-block' }} className="mr-1">Contract Status: </span>
                {/*tslint:disable-next-line: max-line-length*/}
                <span style={{ display: 'inline-block' }}>
                    &nbsp;{activeLeaseTypes?.length > 0 ? OwnershipFormUtil.appendStatus(leasedStatus, activeLeaseTypes) : leasedStatus}&nbsp;
                </span>
            </div>
        );
    };

    const toggleAdditionalOwnerships = () => {
        const newHeight = additionalHeight === 'auto' ? 0 : 'auto';
        if (newHeight === 'auto') {
            setHasExpandedAdditional(true);
        }
        setAdditionalHeight(newHeight);
    };

    const toggleVestingDeed = () => {
        const newHeight = vestingHeight === 'auto' ? 0 : 'auto';
        setVestingHeight(newHeight);
    };

    const handleCategoryOptions = (typeId: number) => {
        let catType = props.categoryTypes?.find(ct => ct.id === typeId);

        return catType?.selections?.map(s => new SelectItem(s.id, s.text)) ?? [];
    };

    return (
        <Card
            badge={`${props.ownership.id}`}
            rightBadgePanel={ownershipBadgeInfo(props.ownership?.activeLeaseTypes ?? [], props.ownership?.leasedStatus ?? '')}
            contextActions={actions}
            newContext={true}
            showMenu={true}
            isHighlighted={isClientLeased}
            className="small-input"
        >
            <div
                key={props.ownership.id}
                style={{ minHeight: '5rem', margin: '0rem 1rem 0 1rem', display: 'flex', justifyContent: 'space-between' }}
            >
                <div style={{ width: '20%', paddingRight: '0.5rem'}}>
                    {props.ownership?.ownerInfo && (
                        <div>
                            <div>
                                <span style={{ fontSize: '1.7rem' }}>
                                    {OwnerUtil.FullOwnershipName(props.ownership.ownerInfo, props.ownership?.isCommunityProperty)}
                                </span>
                            </div>
                            <div>
                                <span>{AddressUtil.concatWithCommas(props.ownership.ownerInfo.addressLine1, props.ownership.ownerInfo.city, props.ownership.ownerInfo.state)}</span>
                            </div>
                        </div>
                    )}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div style={{ display: 'none' }}>
                        {/* <input
                            name={`ownerships[${props.index}].id`}
                            type="number"
                        /> */}
                        <FormTextbox
                            label="NOSHOW"
                            name={`ownerships[${props.index}].id`}
                            type={FormTextBoxDataType.Int}
                        />
                        <FormTextbox
                            label="NOSHOW"
                            name={`ownerships[${props.index}].ownerLookUpId`}
                            type={FormTextBoxDataType.Int}
                        />
                    </div>
                    <div className="mr-1 flex flex-wrap">
                        {props.ownership.ownershipCategories?.map((oc, index) => {
                            return (
                                <div key={`ownershipCategory-${index}`} className="mr-1" style={{ width: '23rem' }}>
                                    <div hidden={true}>
                                        <FormTextbox
                                            label="NOSHOW"
                                            name={`ownerships[${props.index}].ownershipCategories[${index}].id`}
                                            type={FormTextBoxDataType.Int}
                                        />
                                        <FormTextbox
                                            label="NOSHOW"
                                            name={`ownerships[${props.index}].ownershipCategories[${index}].ownershipId`}
                                            type={FormTextBoxDataType.Int}
                                        />
                                        <FormTextbox
                                            label="NOSHOW"
                                            name={`ownerships[${props.index}].ownershipCategories[${index}].categoryTypeId`}
                                            type={FormTextBoxDataType.Int}
                                        />
                                    </div>
                                    <FormSelect
                                        label={oc.categoryTypeName}
                                        name={`ownerships[${props.index}].ownershipCategories[${index}].categorySelectionId`}
                                        items={handleCategoryOptions(oc.categoryTypeId)}
                                        style={{ minWidth: '23rem' }}
                                    />
                                </div>
                            );
                        })}
                        <FormSelect
                            label="Contact Category"
                            name={`ownerships[${props.index}].ownershipContactCategoryId`}
                            items={localContactCategories}
                            style={{ minWidth: '23rem' }}
                            className="mr-1"
                            defaultOption={props?.contactCategories?.find((item) => item.id === props?.ownership?.ownershipContactCategoryId)?.name}
                        />
                        <FormSelect
                            label="Lease Buyer"
                            name={`ownerships[${props.index}].leaseBuyer`}
                            style={{ width: '23rem' }}
                            items={props.leaseBuyers}
                            className="mr-1"
                        />
                        {props.hasNonExecutiveAdditionalOwnership && (
                            <FormTextbox
                                label="Executive Rights"
                                onFocus={props.onInterestFocus}
                                onChange={props.onInterestChange}
                                name={`ownerships[${props.index}].executiveRights`}
                                style={{ width: '23rem' }}
                                className="mr-1"
                                type={FormTextBoxDataType.Float}
                            />
                        )}
                        <FormTextbox
                            label="Interest"
                            onFocus={props.onInterestFocus}
                            onChange={props.onInterestChange}
                            name={`ownerships[${props.index}].decimalInterest`}
                            style={{ width: '23rem' }}
                            className="mr-1"
                            type={FormTextBoxDataType.Float}
                            isHighlighted={isClientLeased && props?.ownership?.leasedStatus !== Constants.contractStatus.OPEN}
                        />                        
                        <FormTextbox
                            label="Net Acres"
                            onFocus={props.onInterestFocus}
                            onChange={props.onInterestChange}
                            name={`ownerships[${props.index}].netAcres`}
                            style={{ width: '23rem' }}
                            type={FormTextBoxDataType.Float}
                            isHighlighted={isClientLeased && props?.ownership?.leasedStatus !== Constants.contractStatus.OPEN}
                        />
                    </div>
                </div>
            </div>

            <div onClick={toggleVestingDeed} style={{ marginBottom: '1rem', marginLeft: '2rem', marginRight: '2rem', borderTop: '1px dashed gray' }}>
                <span style={{ fontWeight: 'bold', fontSize: '1.75rem' }}>{vestingHeight === 'auto' ? '- ' : '+'}</span>
                Vesting Deed
            </div>
            <AnimateHeight duration={300} height={vestingHeight}>
                {vestingDeedRender()}
            </AnimateHeight>

            {props.ownership?.additionalOwnerships?.length > 0 && (
                <>
                    <div onClick={toggleAdditionalOwnerships} style={{ marginBottom: '1rem', marginLeft: '2rem', marginRight: '2rem', borderTop: '1px dashed gray' }}>
                        <span style={{ fontWeight: 'bold', fontSize: '1.75rem' }}>{additionalHeight === 'auto' ? '- ' : '+'}</span>
                        {`Additional Ownerships (${props.ownership.additionalOwnerships.length})`}
                    </div>
                    <AnimateHeight duration={300} height={additionalHeight}>
                        {hasExpandedAdditional && additionalOwnership()}
                    </AnimateHeight>
                </>
            )}
            {props.ownership?.attachedAgreements?.length > 0 && (
                <AttachedAgreements
                    attachedAgreements={props.ownership?.attachedAgreements}
                    ownerLookupId={props.ownership?.ownerLookUpId}
                    navToLeasehold={navToLeasehold}
                />
            )}
        </Card>
    );
};

// Memoized so that re-renders of the parent OwnershipList (e.g. on every
// keystroke while editing interest/acres) only re-render the card whose
// props actually changed, not all 50-200 cards. Relies on OwnershipList
// passing stable prop references (memoized lists + stable callbacks).
export default React.memo(OwnershipCard);



// WEBPACK FOOTER //
// ./src/components/project/sections/TractComponents/OwnershipCard.tsx